import React, { useState, useEffect } from "react";
import { Card, CardBody, Form, Row, Col, Label, Input, FormFeedback, Button, InputGroup, InputGroupText } from "reactstrap";
import { FaUser, FaEnvelope, FaPhone, FaMapMarkerAlt, FaHome, FaGlobe, FaBriefcase, FaIdBadge, FaFileInvoice } from "react-icons/fa";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useSelector, useDispatch } from "react-redux";
import { POST_Address_details } from "../../slices/thunks";
import countries from "./../AddressVerification/Countries";
import { Get_Credentials } from "../../slices/Register/Profile/thunk";

const PersonalDetailsForm = ({ mobile, selectedCategories }) => {
    const dispatch = useDispatch();

    // Redux State
    const data = useSelector((state) => state.Credentials.data); // API returns an array
    const loading = useSelector((state) => state.Credentials.loading);

    // Local State for Prefilled Data
    const [prefilledData, setPrefilledData] = useState(null);

    // Fetch Data from Redux when component mounts
    useEffect(() => {
        dispatch(Get_Credentials());
        console.log(data);
    }, [dispatch]);

    // Update Prefilled Data when Redux `data` changes
    useEffect(() => {
        if (Array.isArray(data) && data.length > 0) {
            const userData = data[0]; // Extract first object
            setPrefilledData({
                licensedTo: userData.LicensedTo || "",
                firstname: userData.ContactPersonName.split(" ")[0] || "",
                lastname: userData.ContactPersonName.split(" ")[1] || "",
                phonenumber: mobile || userData.Mobile || "",
                email: userData.EmailID || "",
                gstin: userData.GSTIN || "",
                gstinregistration: userData.GSTINRegistrationType || "",
                designation: userData.Designation || "",

                addressline1: userData.Address || "",
                addressline2:userData.AddressLine1 || "",
                city: userData.City || "",
                state: userData.State || "",
                country: userData.Country || "",
                zipcode: userData.Zipcode || "",
                categories: Object.keys(selectedCategories || {}),
            });
        }
    }, [data, mobile]);
    const getCountryList = (data) => {
        return Object.values(data).flat().map(item => ({
            country: item.country,
            states: item.states
        }));
    };

    const [countryList] = useState(getCountryList(countries));
    const [states, setStates] = useState([]);
    const handleCountryChange = (e) => {
        const selectedCountry = e.target.value;
        validation.setFieldValue("country", selectedCountry);

        // Find the selected country's states
        const countryData = countryList.find((country) => country.country === selectedCountry);
        setStates(countryData ? countryData.states : []); // Update the states list

        // Reset state field when country changes
        validation.setFieldValue("state", "");
    };

    // Prefill states based on existing user data
    useEffect(() => {
        if (prefilledData?.country) {
            const countryData = countryList.find((c) => c.country === prefilledData.country);
            setStates(countryData ? countryData.states : []);
        }
    }, [prefilledData, countryList]);
    // Formik Validation
    const validation = useFormik({
        enableReinitialize: true,
        initialValues: prefilledData || {
            licensedTo: "",
            firstname: "",
            lastname: "",
            phonenumber: mobile || "",
            email: "",
            gstin: "",
            gstinregistration: "",
            designation: "",
            addressline1:  "",
            addressline2: "",
            city: "",
            state: "",
            country: "",
            zipcode: "",
        },
        validationSchema: Yup.object({
            licensedTo: Yup.string().required("Company Name is required"),
            firstname: Yup.string().required("First Name is required"),
            lastname: Yup.string().required("Last Name is required"),
            phonenumber: Yup.string()
                .matches(/^[0-9]{10}$/, "Phone Number must be exactly 10 digits")
                .required("Phone Number is required"),
            email: Yup.string().email("Invalid email format").required("Email is required"),
            gstin: Yup.string()
                .matches(/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/, "Invalid GSTIN format")
                .required("GSTIN is required"),
            gstinregistration: Yup.string().required("GSTIN registration type required"),
            designation: Yup.string().required("Designation is required"),
            addressline1: Yup.string().required("Address Line 1 is required"),
            addressline2: Yup.string().required("Address Line 2 is required"),
            city: Yup.string().required("City is required"),
            state: Yup.string().required("State is required"),
            country: Yup.string().required("Country is required"),
            zipcode: Yup.string()
                .matches(/^[0-9]{5,6}$/, "Zip Code must be 5 or 6 digits")
                .required("Zip Code is required"),

        }),
        onSubmit: (values) => {
            dispatch(POST_Address_details(values));
        },
    });

    return (
        <Card className="shadow-sm border-0 rounded-3 p-3">
            <CardBody>
                <Form onSubmit={validation.handleSubmit}>
                    <Row className="gy-4">
                        <Col lg={12}>
                            <Label htmlFor="licensedTo" className="fw-bold">Licensed To</Label>
                            <InputGroup>
                                <InputGroupText><FaBriefcase /></InputGroupText>
                                <Input type="text" name="licensedTo" placeholder="Enter Company Name" {...validation.getFieldProps("licensedTo")} invalid={validation.touched.licensedTo && validation.errors.licensedTo} className="rounded-end"/>
                                <FormFeedback>{validation.errors.licensedTo}</FormFeedback>
                            </InputGroup>
                        </Col>

                        <Col lg={6}>
                            <Label className="fw-bold">First Name</Label>
                            <InputGroup>
                                <InputGroupText><FaUser /></InputGroupText>
                                <Input type="text" name="firstname" {...validation.getFieldProps("firstname")} invalid={validation.touched.firstname && validation.errors.firstname} className="rounded-end"/>
                                <FormFeedback>{validation.errors.firstname}</FormFeedback>
                            </InputGroup>
                        </Col>
                        <Col lg={6}>
                            <Label className="fw-bold">Last Name</Label>
                            <InputGroup>
                                <InputGroupText><FaUser /></InputGroupText>
                                <Input type="text" name="lastname" {...validation.getFieldProps("lastname")} invalid={validation.touched.lastname && validation.errors.lastname} className="rounded-end"/>
                                <FormFeedback>{validation.errors.lastname}</FormFeedback>
                            </InputGroup>
                        </Col>

                        <Col lg={6}>
                            <Label className="fw-bold">Email</Label>
                            <InputGroup>
                                <InputGroupText><FaEnvelope /></InputGroupText>
                                <Input type="email" name="email" {...validation.getFieldProps("email")} invalid={validation.touched.email && validation.errors.email} className="rounded-end"/>
                                <FormFeedback>{validation.errors.email}</FormFeedback>
                            </InputGroup>
                        </Col>
                        <Col lg={6}>
                            <Label className="fw-bold">Phone Number</Label>
                            <InputGroup>
                                <InputGroupText><FaPhone /></InputGroupText>
                                <Input type="text" name="phonenumber" {...validation.getFieldProps("phonenumber")} invalid={validation.touched.phonenumber && validation.errors.phonenumber} className="rounded-end"/>
                                <FormFeedback>{validation.errors.phonenumber}</FormFeedback>
                            </InputGroup>
                        </Col>

                        <Col lg={12}>
                            <Label className="fw-bold">GSTIN</Label>
                            <InputGroup>
                                <InputGroupText><FaFileInvoice /></InputGroupText>
                                <Input type="text" name="gstin" placeholder="Enter your GSTIN" {...validation.getFieldProps("gstin")} invalid={validation.touched.gstin && validation.errors.gstin} className="rounded-end"/>
                                <FormFeedback>{validation.errors.gstin}</FormFeedback>
                            </InputGroup>
                        </Col>

                        <Col lg={12}>
                            <Label className="fw-bold">GSTIN Registration Type</Label>
                            <Input type="select" name="gstinregistration" {...validation.getFieldProps("gstinregistration")} invalid={validation.touched.gstinregistration && validation.errors.gstinregistration} className="form-select">
                                <option value="">Select GSTIN Registration Type</option>
                                <option value="Regular">Regular</option>
                                <option value="Composition">Composition</option>
                                <option value="Unregistered">Unregistered</option>
                            </Input>
                            <FormFeedback>{validation.errors.gstinregistration}</FormFeedback>
                        </Col>

                        <Col lg={12}>
                            <Label className="fw-bold">Designation</Label>
                            <InputGroup>
                                <InputGroupText><FaIdBadge /></InputGroupText>
                                <Input type="text" name="designation" placeholder="Enter your designation" {...validation.getFieldProps("designation")} invalid={validation.touched.designation && validation.errors.designation} className="rounded-end"/>
                                <FormFeedback>{validation.errors.designation}</FormFeedback>
                            </InputGroup>
                        </Col>

                        <Col lg={12}>
                            <Label className="fw-bold">Address Line 1</Label>
                            <InputGroup>
                                <InputGroupText><FaHome /></InputGroupText>
                                <Input type="text" name="addressline1" {...validation.getFieldProps("addressline1")} invalid={validation.touched.addressline1 && validation.errors.addressline1} className="rounded-end"/>
                                <FormFeedback>{validation.errors.addressline1}</FormFeedback>
                            </InputGroup>
                        </Col>
                        <Col lg={12}>
                            <Label className="fw-bold">Address Line 2</Label>
                            <InputGroup>
                                <InputGroupText><FaHome /></InputGroupText>
                                <Input type="text" name="addressline2" {...validation.getFieldProps("addressline2")} invalid={validation.touched.addressline2 && validation.errors.addressline2} className="rounded-end"/>
                                <FormFeedback>{validation.errors.addressline2}</FormFeedback>
                            </InputGroup>
                        </Col>

                        <Col lg={4}>
                            <Label className="fw-bold">City</Label>
                            <InputGroup>
                                <InputGroupText><FaMapMarkerAlt /></InputGroupText>
                                <Input type="text" name="city" {...validation.getFieldProps("city")} invalid={validation.touched.city && validation.errors.city} className="rounded-end"/>
                                <FormFeedback>{validation.errors.city}</FormFeedback>
                                </InputGroup>
                            </Col>
                            <Col lg={4}>
                                <div className="mb-3">
                                    <Label htmlFor="country" className="form-label">Country</Label>
                                    <Input
                                        type="select"
                                        name="country"
                                        id="country"
                                        className="form-control"
                                        onChange={handleCountryChange}
                                        onBlur={validation.handleBlur}
                                        value={validation.values.country || ""}
                                        invalid={validation.touched.country && validation.errors.country ? true : false}
                                    >
                                        <option value="">Select Country</option>
                                        {countryList.map((item, index) => (
                                            <option key={index} value={item.country}>{item.country}</option>
                                        ))}
                                    </Input>
                                    {validation.touched.country && validation.errors.country && (
                                        <FormFeedback type="invalid">{validation.errors.country}</FormFeedback>
                                    )}
                                </div>
                            </Col>

                            <Col lg={4}>
                                <div className="mb-3">
                                    <Label htmlFor="state" className="form-label">State</Label>
                                    <Input
                                        type="select"
                                        name="state"
                                        id="state"
                                        className="form-control"
                                        onChange={(e) => validation.setFieldValue("state", e.target.value)}
                                        onBlur={validation.handleBlur}
                                        value={validation.values.state || ""}
                                        invalid={validation.touched.state && validation.errors.state}
                                        disabled={!states.length} // Disable if no states are available
                                    >
                                        <option value="">Select State</option>
                                        {states.map((state, index) => (
                                            <option key={index} value={state}>{state}</option>
                                        ))}
                                    </Input>
                                    {validation.touched.state && validation.errors.state && (
                                        <FormFeedback type="invalid">{validation.errors.state}</FormFeedback>
                                    )}
                                </div>
                            </Col>;

                        <Col lg={12} className="text-end mt-4">
                            <Button type="submit" color="primary" className="me-2 px-4 py-2 fw-bold shadow-sm">Update</Button>
                            <Button type="button" color="secondary" onClick={validation.handleReset} className="px-4 py-2 fw-bold shadow-sm">Cancel</Button>
                        </Col>
                    </Row>
                </Form>
            </CardBody>
        </Card>
    );
};


export default PersonalDetailsForm;
